import React from "react";
import { CreditCard, Trash2, Copy, Check, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";

const THEMES = {
  midnight: "from-slate-900 via-indigo-950 to-slate-900",
  emerald: "from-emerald-900 via-teal-900 to-slate-900",
  sunset: "from-rose-900 via-orange-900 to-slate-900",
  royal: "from-violet-900 via-purple-950 to-slate-900",
  graphite: "from-zinc-800 via-zinc-900 to-black",
};

const BRAND_LABEL = {
  visa: "VISA",
  mastercard: "MASTERCARD",
  amex: "AMEX",
  discover: "DISCOVER",
  unknown: "CARD",
};

export default function VaultCard({ card, onDelete }) {
  const [revealed, setRevealed] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(card.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      className="group relative"
    >
      <div
        className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${THEMES[card.color_theme] || THEMES.midnight} p-6 shadow-2xl ring-1 ring-white/10`}
      >
        {/* shimmer */}
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-white/5 blur-2xl" />

        <div className="relative flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/50">
              {card.nickname || "Voult Card"}
            </p>
            <p className="mt-1 text-sm text-white/80">{card.cardholder_name}</p>
          </div>
          <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold tracking-widest text-white/90">
            {BRAND_LABEL[card.brand] || "CARD"}
          </span>
        </div>

        <div className="relative mt-8 flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-white/40" />
          <p className="font-mono text-lg tracking-wider text-white">
            {revealed ? card.masked_number : "•••• •••• ••••"}
            <span className="text-white"> {card.last4}</span>
          </p>
        </div>

        <div className="relative mt-6 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40">Expires</p>
            <p className="font-mono text-sm text-white/90">
              {card.expiry_month}/{card.expiry_year}
            </p>
          </div>
          <div className="max-w-[55%] text-right">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Token</p>
            <p className="truncate font-mono text-xs text-white/70">{card.token}</p>
          </div>
        </div>
      </div>

      {/* actions */}
      <div className="mt-3 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRevealed((r) => !r)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
          >
            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {revealed ? "Hide" : "Reveal"}
          </button>
          <button
            onClick={copyToken}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy token"}
          </button>
        </div>
        <button
          onClick={() => onDelete(card)}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-rose-500 transition hover:bg-rose-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </button>
      </div>
    </motion.div>
  );
}